"use client"

import { useEffect } from "react"
import { usePostHog } from "@/lib/posthog/hooks"

/**
 * Tracks visits that originated from an AI answer engine (ChatGPT, Perplexity,
 * Gemini, Copilot, Claude, etc.) so we can measure AEO/GEO payoff separately
 * from classic organic search.
 *
 * Answer engines pass a referrer host (and sometimes a `utm_source`) we can
 * match. We fire a single PostHog event per page load when we detect one, plus
 * register it as a person/session property so downstream conversions (Lead,
 * Schedule) can be attributed back to AI referral.
 */
const AI_REFERRER_HOSTS: { match: RegExp; source: string }[] = [
  { match: /(^|\.)chatgpt\.com$/i, source: "chatgpt" },
  { match: /(^|\.)chat\.openai\.com$/i, source: "chatgpt" },
  { match: /(^|\.)openai\.com$/i, source: "openai" },
  { match: /(^|\.)perplexity\.ai$/i, source: "perplexity" },
  { match: /(^|\.)gemini\.google\.com$/i, source: "gemini" },
  { match: /(^|\.)bard\.google\.com$/i, source: "gemini" },
  { match: /(^|\.)copilot\.microsoft\.com$/i, source: "copilot" },
  { match: /(^|\.)bing\.com$/i, source: "bing-copilot" },
  { match: /(^|\.)claude\.ai$/i, source: "claude" },
  { match: /(^|\.)you\.com$/i, source: "you" },
  { match: /(^|\.)phind\.com$/i, source: "phind" },
  { match: /(^|\.)poe\.com$/i, source: "poe" },
]

// utm_source values AI tools sometimes append to outbound links.
const AI_UTM_SOURCES = new Set([
  "chatgpt.com",
  "chatgpt",
  "openai",
  "perplexity",
  "gemini",
  "copilot",
  "claude",
])

function detectAiSource(): string | null {
  try {
    const params = new URLSearchParams(window.location.search)
    const utm = (params.get("utm_source") || "").toLowerCase()
    if (utm && AI_UTM_SOURCES.has(utm)) return utm.replace(/\.com$/, "")

    const ref = document.referrer
    if (!ref) return null
    const host = new URL(ref).hostname
    for (const { match, source } of AI_REFERRER_HOSTS) {
      if (match.test(host)) return source
    }
  } catch {
    // Malformed referrer/URL — ignore.
  }
  return null
}

export function AiReferralTracker() {
  const { captureEvent, posthog } = usePostHog()

  useEffect(() => {
    const source = detectAiSource()
    if (!source) return

    captureEvent("ai_referral_visit", {
      ai_source: source,
      landing_path: window.location.pathname,
      referrer: document.referrer || undefined,
    })

    // Persist so downstream Lead/Schedule conversions attribute to AI referral.
    posthog?.register({ ai_referral_source: source })
  }, [captureEvent, posthog])

  return null
}
